import { Button } from '@/components/ui/button';
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import {
  useGetServerlessProjects,
  useServerlessStorageCopy,
} from '@/service/serverless-gpu/hook';
import { StatusServerlessProject } from '@/service/serverless-gpu/type';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { toast } from 'sonner';
import { toastSomethingError } from '@/components/ui/toast-content';

type DialogCopyFileProps = {
  projectId: string;
  filePath: string;
  onSuccess: () => void;
};

const DialogCopyFile = ({
  projectId,
  filePath,
  onSuccess,
}: DialogCopyFileProps) => {
  const { mutateAsync: copyFile, isPending: isCopying } =
    useServerlessStorageCopy({
      onSuccess: () => {
        toast.success('File copied successfully');
        onSuccess();
      },
      onError: (error) => {
        toastSomethingError(error);
      },
    });

  const { data, isLoading } = useGetServerlessProjects({});

  const projects = useMemo(
    () =>
      data?.projects?.filter(
        (project) => project.status === StatusServerlessProject.Active
      ),
    [data]
  );

  const formSchema = z.object({
    destinationPath: z.string(),
    destinationProjectId: z.string().min(1, {
      message: 'Destination Project is required',
    }),
  });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      destinationPath: '',
      destinationProjectId: projectId,
    },
  });

  useEffect(() => {
    if (data) {
      form.setValue('destinationProjectId', projectId);
    }
  }, [data, projectId]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    await copyFile({
      original_path: `${projectId}:${filePath}`,
      destination_path: `${data.destinationProjectId}:${data.destinationPath}`,
    });
  };

  return (
    <DialogContent>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Copy File to</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <FormField
              control={form.control}
              name="destinationProjectId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Destination Project</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a project" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {projects?.map((project) => (
                        <SelectItem
                          key={project.project_id}
                          value={project.project_id}
                        >
                          {project.project_id}{' '}
                          {project.project_id === projectId &&
                            '(Current Project)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="destinationPath"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Destination Path</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter destination path" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>

            <Button disabled={isCopying} type="submit">
              Copy
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
};

export default DialogCopyFile;
